import { useEffect, useRef, useState } from "react";

/**
 * WebHID radio-controller support (e.g. Radiomaster Zorro / any EdgeTX radio in
 * "USB Joystick" mode). The Gamepad API often won't surface these; WebHID reads
 * the device directly (same approach that connected the SpaceMouse to memviz).
 * Works on localhost (secure context). Channels are parsed from the HID report
 * descriptor and normalized to -1..1; the user maps channels -> flight axes.
 */

export type RadioMapping = {
  throttle: number; yaw: number; pitch: number; roll: number; arm: number; rec: number; // channel indices (-1 = unmapped)
  inv: { throttle: boolean; yaw: boolean; pitch: boolean; roll: boolean; arm: boolean; rec: boolean };
};

// EdgeTX default AETR order: Ch1 aileron(roll), Ch2 elevator(pitch), Ch3 throttle, Ch4 rudder(yaw)
export const DEFAULT_MAPPING: RadioMapping = {
  roll: 0, pitch: 1, throttle: 2, yaw: 3, arm: -1, rec: -1,
  inv: { throttle: false, yaw: false, pitch: false, roll: false, arm: false, rec: false },
};

const LS = "swarmsim_radio_v1";
type Field = { reportId: number; off: number; size: number; lmin: number; lmax: number };

/* eslint-disable @typescript-eslint/no-explicit-any */

function buildFields(device: any): Field[] {
  const fields: Field[] = [];
  for (const col of device.collections ?? []) {
    for (const rep of col.inputReports ?? []) {
      let bit = 0;
      for (const item of rep.items ?? []) {
        const count = item.reportCount ?? 0;
        const size = item.reportSize ?? 0;
        const usable = (item.isAbsolute ?? true) && !(item.isArray ?? false) && !(item.isConstant ?? false) && size >= 8 && size <= 16;
        for (let c = 0; c < count; c++) {
          if (usable) fields.push({ reportId: rep.reportId ?? 0, off: bit, size, lmin: item.logicalMinimum ?? 0, lmax: item.logicalMaximum ?? ((1 << size) - 1) });
          bit += size;
        }
      }
    }
  }
  return fields;
}

function readBits(data: DataView, off: number, size: number): number {
  let v = 0;
  for (let i = 0; i < size; i++) {
    const b = off + i;
    if ((b >> 3) < data.byteLength) v |= ((data.getUint8(b >> 3) >> (b & 7)) & 1) << i;
  }
  return v;
}

function parseReport(data: DataView, fields: Field[], reportId: number): number[] {
  const relevant = fields.filter((f) => f.reportId === reportId || f.reportId === 0);
  const src = relevant.length ? relevant : fields;
  if (src.length >= 2) {
    return src.map((f) => {
      let v = readBits(data, f.off, f.size);
      if (f.lmin < 0 && v >= 1 << (f.size - 1)) v -= 1 << f.size; // two's complement
      const range = f.lmax - f.lmin;
      const n = range > 0 ? ((v - f.lmin) / range) * 2 - 1 : 0;
      return Math.max(-1, Math.min(1, n));
    });
  }
  // fallback: treat report bytes as centered uint8 channels
  const out: number[] = [];
  for (let i = 0; i < Math.min(data.byteLength, 16); i++) out.push((data.getUint8(i) - 128) / 128);
  return out;
}

export function useRadio() {
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState("");
  const [count, setCount] = useState(0);
  const [error, setError] = useState("");
  const [mapping, setMapping] = useState<RadioMapping>(() => {
    try {
      const s = localStorage.getItem(LS);
      if (s) {
        const j = JSON.parse(s) as Partial<RadioMapping>;
        return { ...DEFAULT_MAPPING, ...j, inv: { ...DEFAULT_MAPPING.inv, ...j.inv } };
      }
    } catch { /* ignore */ }
    return DEFAULT_MAPPING;
  });
  const channels = useRef<number[]>([]);
  const deviceRef = useRef<any>(null);
  const countRef = useRef(0);

  useEffect(() => { try { localStorage.setItem(LS, JSON.stringify(mapping)); } catch { /* ignore */ } }, [mapping]);

  function attach(device: any) {
    const fields = buildFields(device);
    device.oninputreport = (e: any) => {
      channels.current = parseReport(e.data, fields, e.reportId ?? 0);
      if (channels.current.length !== countRef.current) { countRef.current = channels.current.length; setCount(channels.current.length); }
    };
    deviceRef.current = device;
    setDeviceName(device.productName || "radio");
    setConnected(true);
  }

  async function connect() {
    setError("");
    const hid = (navigator as any).hid;
    if (!hid) { setError("This browser has no WebHID — use Chrome or Edge."); return; }
    try {
      const devices = await hid.requestDevice({ filters: [{ usagePage: 0x01 }] });
      const device = devices?.[0];
      if (!device) return;
      if (!device.opened) await device.open();
      attach(device);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  // auto-reconnect a previously-granted device + handle disconnect
  useEffect(() => {
    const hid = (navigator as any).hid;
    if (!hid) return;
    hid.getDevices?.().then((ds: any[]) => {
      const d = ds?.find((x) => x);
      if (d) (d.opened ? Promise.resolve() : d.open()).then(() => attach(d)).catch(() => {});
    });
    const onDisc = (e: any) => {
      if (e.device === deviceRef.current) { setConnected(false); channels.current = []; countRef.current = 0; setCount(0); }
    };
    hid.addEventListener?.("disconnect", onDisc);
    return () => hid.removeEventListener?.("disconnect", onDisc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { connected, deviceName, count, error, mapping, setMapping, channels, connect };
}
